import { getShopNaviList } from "@/api/shopPlus";
import { getRatio, pxtorem } from "@/utils/pxtorem";
import Track from "@/utils/track";
// import { VerticalTabComp } from "@akc/mx-taro-h5-components";
import VerticalTabComp from "@/components/vertical-tab";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  useCouponIds,
  useProductStatus,
  usePromoActivityIds,
  useSelectedActivity,
  useSelectedActivityIdList,
  useSetCouponIds,
  useSetFilterInfo,
  useSetProductStatus,
  useSetPromoActivityIds,
  useSetSelectedSortInfo,
  useSetVerticalTabItem,
  useShowProductStatus,
} from "../hooks/useActivityProduct";
import ProductList from "./ProductList";
import ProductStatusTab from "./ProductStatusTab";
import SortAndFilter from "./SortAndFilter";
import styles from "./VerticalNavComp.module.less";
// 定义组件引用类型
type VerticalNavCompRef = {
  resetFilter?: () => void;
};

// 使用forwardRef改造组件
const VerticalNavComp = forwardRef<VerticalNavCompRef, { hasActivityList: boolean }>(({ hasActivityList }, ref) => {
  const [navTabList, setNavTabList] = useState([]);
  const [navSelectedIndex, setNavSelectedIndex] = useState<number>(0);
  const [naviFloat, setNaviFloat] = useState(false);
  const stickyListenRef = useRef<HTMLDivElement | null>(null);
  const filterRef = useRef<HTMLDivElement | null>(null);
  const filterDomRef = useRef<any>(null);
  const naviListRef = useRef<HTMLDivElement | null>(null); // 添加导航列表容器的ref
  const stickyOffsetTopRef = useRef<number>(0);

  const selectedActivity = useSelectedActivity();

  const setFilterInfo = useSetFilterInfo();
  const setSelectedSortInfo = useSetSelectedSortInfo();

  // 商品状态相关
  const showProductStatus = useShowProductStatus();
  const productStatus = useProductStatus();
  const setProductStatus = useSetProductStatus();
  const selectedActivityIdList = useSelectedActivityIdList();

  const setVerticalTabItem = useSetVerticalTabItem();
  const [search] = useSearchParams();
  const track = Track();

  // 获取导航类项目信息
  useEffect(() => {
    if (!selectedActivityIdList || selectedActivityIdList.length === 0) {
      return;
    }
    let params = {
      pageId: 5,
      activityIds: selectedActivityIdList,
      activityTypeList: [2],
      activityTypeGroup: 2,
    };
    getShopNaviList(params).then(res => {
      setNavTabList(res.data);
      setNavSelectedIndex(0);
      setVerticalTabItem({ ...res.data[0] });
      // 重置导航列表容器的滚动位置
      if (naviListRef.current) {
        naviListRef.current.scrollTop = 0;
      } else {
        // 如果 ref 还没有被设置，尝试通过 ID 获取元素
        const naviListContainer = document.getElementById("navi_list_container");
        if (naviListContainer) {
          naviListContainer.scrollTop = 0;
        }
      }
    });
    setTimeout(() => {
      if (stickyListenRef.current) {
        // 获取元素相对于视口的位置
        const rect = stickyListenRef.current.getBoundingClientRect();
        // 加上当前的滚动位置，得到元素距离页面顶部的绝对距离
        const offsetTop = rect.top;
        stickyOffsetTopRef.current = offsetTop;
      }
    }, 500);
  }, [selectedActivityIdList]);

  // 初始化字体大小
  useEffect(() => {
    // 监听滚动，控制导航栏吸顶
    const observer = new IntersectionObserver(
      entries => {
        const { intersectionRatio, boundingClientRect } = entries[0];
        if (intersectionRatio === 0 && boundingClientRect.top < 300) {
          setNaviFloat(true);
        } else {
          setNaviFloat(false);
        }
      },
      {
        threshold: [0, 0.5, 1],
        rootMargin: `-${hasActivityList ? 152 : 88}px 0px 0px 0px`,
      },
    );
    stickyListenRef.current && observer.observe(stickyListenRef.current);
  }, []);

  // 设置滚动条位置
  const setScrollTop = () => {
    // 如果处于吸顶状态，将滚动位置设置为记录的吸顶位置
    if (naviFloat) {
      window.scrollTo({
        top: stickyOffsetTopRef.current - (hasActivityList ? 152 * getRatio() : 88 * getRatio()),
        behavior: "instant",
      });
    }
  };

  // 导航项点击处理
  const naviActiveFn = (item: any) => {
    setVerticalTabItem({ ...item });
    setFilterInfo(undefined);
    filterDomRef.current?.resetFilter?.();
    setScrollTop();
    if (track.track) {
      const params = {
        page_name: "品牌活动",
        previous_page_name: search.get("page_name"),
        tab_name: selectedActivity?.activityAggrTitle,
        tab_rank: (selectedActivity?.rank ?? 0) + 1,
        tab_id: selectedActivityIdList.join(","),
        activity_id: selectedActivityIdList.join(","),
        resource_type: "纵向导航",
        btn_name: item.itemName,
      };
      track.track("resource_click", params);
    }
  };

  // 商品状态变化处理
  const onStatusChange = useCallback(
    (status: number) => {
      setProductStatus(status === productStatus ? null : status);

      // 滚动到顶部
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },
    [productStatus, setProductStatus],
  );

  // 使用useImperativeHandle暴露resetFilter方法
  useImperativeHandle(ref, () => ({
    resetFilter: () => {
      // 重置筛选条件
      filterDomRef.current?.resetFilter?.();
      // 重置筛选状态
      setFilterInfo(undefined);
    }
  }));
  
  return (
    <div className={styles.container}>
      <div
        className={`${naviFloat ? styles.fixedFilterContainer : styles.filterContainer}`}
        style={{ top: hasActivityList ? pxtorem(108) : pxtorem(44) }}
        ref={filterRef}>
        {/* 筛选模块 */}
        <SortAndFilter
          ref={filterDomRef}
          isShowVerticalTab={true}
          brandData={{
            brandId: selectedActivity?.brandId,
            activityList: selectedActivity ? [selectedActivity] : [],
          }}
          onSortFilterChange={setScrollTop}
        />
      </div>

      <div className={styles.productList}>
        <div
          ref={stickyListenRef}
          style={{
            position: "absolute",
            top: naviFloat ? filterRef.current?.clientHeight : 0,
            height: "1px",
            width: "100%",
            background: "#FFF",
          }}></div>
        {/* 导航模块 */}
        <div
          className={`${styles.naviList} ${naviFloat ? (hasActivityList ? styles.naviFloat : styles.naviFloatNoActivity) : ""}`}
          id="navi_list_container"
          ref={naviListRef}>
          <VerticalTabComp
            tabList={navTabList}
            selectedIndex={navSelectedIndex}
            onAction={(item: any, index: number) => {
              naviActiveFn(item);
              setNavSelectedIndex(index);
            }}
          />
        </div>
        {/* 商品列表内容 */}
        <div
          className={styles.listBody}
          style={
            naviFloat
              ? {
                  marginLeft: pxtorem(48),
                  marginTop: pxtorem(44),
                  minHeight: "88vh",
                }
              : {}
          }
          id="list-body">
          {/* 商品状态 */}
          <ProductStatusTab
            verticalNavGray={true}
            brandData={{
              brandId: selectedActivity?.brandId,
              activityList: selectedActivity ? [selectedActivity] : [],
            }}
          />
          {/* 商品列表 */}
          <ProductList hasSidebar={true} provideBackground={false} />
        </div>
      </div>
    </div>
  );
});

export default VerticalNavComp;
